import React from "react";
import { Flame, Check, Gift } from "lucide-react";
import Mandala from "@/components/Mandala";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { OPENING, OPENING_CLOSER, WHO_FOR, EXPERIENCES, TREASURES, ITINERARY } from "./winterContent";

export const Overline = ({ children, light }) => (
  <p className={`overline mb-5 ${light ? "text-aviana-goldSoft" : ""}`}>{children}</p>
);

export function Opening({ img }) {
  return (
    <section className="py-24 md:py-32 bg-aviana-paper" data-testid="ws-opening">
      <div className="container-aviana grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="rounded-t-[999px] rounded-b-[2rem] overflow-hidden ring-1 ring-aviana-gold/30 aspect-square">
            <img src={img("opening")} alt="The log cabin–style ranch in Galena, Illinois, glowing with candlelight in the snow" loading="lazy" className="w-full h-full object-cover object-[52%_center]" />
          </div>
          <p className="mt-5 font-serif italic text-aviana-muted text-[15px]">The ranch · Galena, Illinois</p>
        </div>
        <div className="lg:col-span-7">
          <Overline>You Are Invited</Overline>
          <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.04]">For a few precious days, there is <em className="italic gold-text">nowhere else</em> you need to be.</h2>
          <div className="mt-10 space-y-6 text-aviana-inkSoft text-[16px] md:text-[17px] leading-[1.85]">
            {OPENING.map((p, i) => (
              <p key={i} className={i === 0 ? "first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-aviana-plum" : ""}>{p}</p>
            ))}
            <p className="font-serif italic font-semibold text-aviana-plum text-[20px] md:text-[22px] leading-[1.5] pt-2" data-testid="ws-opening-closer">{OPENING_CLOSER}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FireBand({ src, caption }) {
  return (
    <section className="relative h-[52vh] md:h-[64vh] overflow-hidden" data-testid="ws-fire-band">
      <img src={src} alt="A crackling fire in a cozy stone fireplace" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-aviana-ink/80 via-aviana-ink/20 to-transparent" />
      <p className="absolute bottom-10 left-0 right-0 container-aviana font-serif italic text-aviana-cream text-2xl md:text-4xl max-w-3xl leading-snug">{caption}</p>
    </section>
  );
}

export function WhoFor({ img }) {
  return (
    <section className="py-24 md:py-32 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="ws-who-for">
      <Mandala size={520} variant="rose" className="absolute -left-40 -bottom-40 text-aviana-goldSoft/10 hidden md:block" />
      <div className="container-aviana grid md:grid-cols-2 gap-14 items-center relative">
        <div>
          <Overline light>Who This Is For</Overline>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">A seat by the fire is waiting — <em className="italic text-aviana-goldSoft">for you, and the women you love.</em></h2>
          <ul className="mt-10 space-y-5">
            {WHO_FOR.map((w) => (
              <li key={w} className="flex gap-4 text-[16px] leading-[1.7] text-aviana-cream/90"><Flame size={18} className="text-aviana-goldSoft mt-1 flex-shrink-0" />{w}</li>
            ))}
          </ul>
          <p className="mt-10 font-serif italic text-aviana-goldSoft text-[17px]">We keep our circle small so every woman is truly seen, held and known.</p>
        </div>
        <div className="rounded-[2rem] overflow-hidden aspect-[4/5] ring-1 ring-aviana-goldSoft/30">
          <img src={img("who-for")} alt="Candles glowing in a dark, warm room" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}

export function Experiences({ img }) {
  return (
    <section className="py-24 md:py-32 bg-aviana-cream" data-testid="ws-experiences">
      <div className="container-aviana">
        <div className="max-w-2xl mb-16">
          <Overline>What We'll Share Together</Overline>
          <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.04]">Rest. Reconnect. <em className="italic gold-text">Create. Remember.</em></h2>
        </div>
        <div className="space-y-20">
          {EXPERIENCES.map((e, i) => (
            <div key={e.key} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`ws-${e.key}`}>
              <div className="rounded-[2rem] overflow-hidden aspect-[4/3] ring-1 ring-aviana-gold/25">
                <img src={img(e.key)} alt={e.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase text-aviana-gold mb-3">{String(i + 1).padStart(2, "0")}{e.note ? ` · ${e.note}` : ""}</p>
                <h3 className="font-display text-3xl md:text-4xl text-aviana-ink">{e.title}</h3>
                <ul className="mt-7 space-y-3">
                  {e.items.map((it) => (
                    <li key={it} className="flex gap-3 text-aviana-inkSoft text-[15px] leading-[1.65]"><Check size={16} className="text-aviana-plum mt-1 flex-shrink-0" />{it}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Belonging({ img }) {
  return (
    <section className="py-20 md:py-28 bg-aviana-surface" data-testid="ws-belonging">
      <div className="container-aviana max-w-6xl grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-7 rounded-[2rem] overflow-hidden aspect-[3/2] ring-1 ring-aviana-gold/25">
          <img src={img("belonging")} alt="Women holding hands in a sister circle" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-5">
          <Overline>You Belong Here</Overline>
          <p className="font-serif italic text-aviana-ink text-2xl md:text-3xl leading-[1.45]">Hand in hand, we remember that we were never meant to carry it all alone.</p>
          <p className="mt-6 text-aviana-inkSoft text-[16px] leading-[1.8]">Whether you arrive with your mother, your daughter, your dearest friend or simply yourself, you'll be welcomed into the circle like family — held, celebrated and cared for.</p>
        </div>
      </div>
    </section>
  );
}

export function Treasures({ img }) {
  return (
    <section className="py-24 md:py-28 bg-aviana-paper" data-testid="ws-treasures">
      <div className="container-aviana max-w-5xl grid md:grid-cols-5 gap-12 items-center">
        <div className="md:col-span-2 rounded-t-[999px] rounded-b-[1.5rem] overflow-hidden aspect-[3/4] ring-1 ring-aviana-gold/30">
          <img src={img("treasures")} alt="A handcrafted wooden cutting board" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-3">
          <Overline>Your Take-Home Treasures</Overline>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">Little pieces of our time together <em className="italic gold-text">to carry home.</em></h2>
          <ul className="mt-9 space-y-4">
            {TREASURES.map((t) => (
              <li key={t} className="flex gap-4 text-aviana-inkSoft text-[16px]"><Gift size={17} className="text-aviana-gold mt-0.5 flex-shrink-0" />{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function Itinerary({ img }) {
  return (
    <section className="py-24 md:py-32 bg-aviana-surface" data-testid="ws-itinerary">
      <div className="container-aviana grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Overline>Retreat Itinerary</Overline>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">How our days together <em className="italic gold-text">gently unfold.</em></h2>
          <p className="mt-6 font-serif italic text-aviana-muted text-[16px] leading-[1.7]" data-testid="ws-itinerary-tentative">This is a tentative itinerary — the rhythm of our days may shift gently with the weather, the horses and the needs of our circle.</p>
          <div className="mt-10 rounded-[2rem] overflow-hidden aspect-[4/5] ring-1 ring-aviana-gold/25 hidden lg:block">
            <img src={img("itinerary")} alt="The ranch kitchen where Holly prepares every meal" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </div>
        <Accordion type="single" collapsible defaultValue="day-0" className="lg:col-span-8 space-y-4">
          {ITINERARY.map((d, i) => (
            <AccordionItem key={d.day} value={`day-${i}`} className={`border rounded-2xl px-6 md:px-8 ${d.solstice ? "bg-aviana-plumDeep border-aviana-gold/50 text-aviana-cream" : "bg-aviana-cream border-aviana-border"}`} data-testid={`ws-itinerary-day-${i + 1}`}>
              <AccordionTrigger className="py-6 hover:no-underline text-left">
                <span>
                  <span className="block text-[11px] tracking-[0.26em] uppercase text-aviana-gold">{d.date}</span>
                  <span className={`block font-display text-2xl md:text-3xl mt-1 ${d.solstice ? "text-aviana-goldSoft" : "text-aviana-ink"}`}>{d.day}</span>
                  <span className={`block font-serif italic text-[15px] mt-1 font-normal ${d.solstice ? "text-aviana-cream/85" : "text-aviana-muted"}`}>{d.intro}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-7">
                {d.blocks.map((b, j) => (
                  <div key={j} className={j ? "mt-7" : ""}>
                    {b.label && <p className="overline-plum mb-3">{b.label}</p>}
                    <dl className={`divide-y ${d.solstice ? "divide-aviana-cream/15" : "divide-aviana-border/70"}`}>
                      {b.rows.map(([t, a]) => (
                        <div key={t + a} className="grid grid-cols-[120px_1fr] md:grid-cols-[170px_1fr] gap-4 py-3 text-[14px]">
                          <dt className={`font-medium ${d.solstice ? "text-aviana-goldSoft" : "text-aviana-plum"}`}>{t}</dt>
                          <dd className={`leading-[1.65] ${d.solstice ? "text-aviana-cream/90" : "text-aviana-inkSoft"}`}>{a}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
